import { useEffect, useState } from 'react'
import { Share2, Users } from 'lucide-react'
import { useAppStore } from '@core/store'
import { useAuth } from '@core/auth/useAuth'
import {
  abrirJanelaPartilhar,
  tirarAbertura,
  useAberturaPendente,
  useComigo,
} from '@core/partilhas/partilhasEstado'
import { doTipo, modulosNaInterface, origemDoItem } from '@core/partilhas/partilhasRegras'
import { textoDoEstado } from './clashPartilha'
import {
  entrarNaTriagem,
  motorDaPartilha,
  partilhaDisponivel,
  partilharTriagem,
  sairDaTriagem,
} from './clashPartilhaControlo'
import { pessoasDistintas, quemEdita, useClashPartilha } from './clashPartilhaEstado'
import {
  listarTriagens,
  modelosEmComum,
  verTriagem,
  type ProjetoDeConflitos,
} from './clashPartilhaPorta'
import EditorEspecialidades from '@core/notificacoes/EditorEspecialidades'

// ---------------------------------------------------------------------------
// A barra da triagem partilhada, no topo do banco de trabalho dos
// conflitos: o estado ("Partilhada · ao vivo · 3 pessoas", "Sem ligação ·
// alterações por enviar (2)"), quem esta, com quem esta partilhada, e os
// gestos (partilhar com o workspace e/ou pessoas, "Partilhar com...",
// entrar numa triagem do workspace ou partilhada comigo, sair). So
// aparece com sessao: com o modulo Conflitos no workspace, ou com
// triagens partilhadas de outro workspace (essas so se abrem, nao se
// criam). Em modo local nao ha onde partilhar.
// ---------------------------------------------------------------------------

const BOTAO =
  'rounded border border-[var(--color-border)] px-1.5 py-0.5 hover:text-[var(--color-text)] disabled:opacity-40'

function corDoEstado(estado: string): string {
  if (estado === 'ao-vivo') return 'bg-[var(--color-ok-text)]'
  if (estado === 'sem-ligacao') return 'bg-[var(--color-error-text)]'
  return 'bg-[var(--color-warn-text)]'
}

function dataCurta(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-PT')
}

export default function ClashPartilhaBarra() {
  const { mode, modules, partilhados, user, workspace } = useAuth()
  const comModulo = mode === 'cloud' && modules.includes('clash')
  const disponivel = mode === 'cloud' && modulosNaInterface(modules, partilhados).includes('clash')
  const p = useClashPartilha()
  const models = useAppStore((s) => s.models)
  const pushToast = useAppStore((s) => s.pushToast)
  const [aPartilhar, setAPartilhar] = useState(false)
  const [codigo, setCodigo] = useState('')
  const [nome, setNome] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [erroForm, setErroForm] = useState<string | null>(null)
  const [lista, setLista] = useState<ProjetoDeConflitos[] | null>(null)
  const [aListar, setAListar] = useState(false)
  // O mapa especialidade -> pessoas da triagem partilhada (quem recebe
  // as notificações de "atribuído").
  const [verEspecialidades, setVerEspecialidades] = useState(false)
  // Sem escolha, a triagem fica só para quem a partilha, e a janela
  // "Partilhar com..." abre logo a seguir para escolher.
  const [workspaceInteiro, setWorkspaceInteiro] = useState(false)
  // Com quem esta partilhada a triagem activa.
  const [info, setInfo] = useState<ProjetoDeConflitos | null>(null)
  const [lerInfo, setLerInfo] = useState(0)
  const activaId = p.activa?.projetoId ?? null
  const comigo = useComigo(disponivel && !!lista)
  const pendente = useAberturaPendente()

  useEffect(() => {
    if (!disponivel || !activaId) return
    let vivo = true
    void verTriagem(activaId)
      .then((t) => {
        if (vivo) setInfo(t)
      })
      .catch(() => {
        if (vivo) setInfo(null)
      })
    return () => {
      vivo = false
    }
  }, [disponivel, activaId, lerInfo])

  // "Abrir" em Partilhados comigo: entra na triagem quando o banco dos
  // conflitos estiver ligado (o contexto da partilha chega depois de o
  // painel abrir).
  useEffect(() => {
    if (!disponivel || !pendente || pendente.tipo !== 'triagem') return
    const t = window.setInterval(() => {
      if (!partilhaDisponivel()) return
      window.clearInterval(t)
      const item = tirarAbertura('triagem')
      if (!item) return
      const codigo = item.nome ?? 'Triagem'
      if (entrarNaTriagem({ id: item.recurso, codigo, nome: null }, (m) => window.confirm(m))) {
        pushToast(`Entrou na triagem partilhada "${codigo}".`, { type: 'success' })
      }
    }, 200)
    return () => window.clearInterval(t)
  }, [disponivel, pendente, pushToast])

  if (!disponivel) return null

  const onPartilhar = async () => {
    setOcupado(true)
    setErroForm(null)
    try {
      const r = await partilharTriagem(codigo.trim(), nome.trim() || null, workspaceInteiro)
      setAPartilhar(false)
      setCodigo('')
      setNome('')
      pushToast(
        workspaceInteiro
          ? `Triagem partilhada no workspace como "${r.codigo}".`
          : `Triagem "${r.codigo}" criada, por agora só para si: escolha com quem a partilha.`,
        { type: 'success' },
      )
      if (r.partilha_id) {
        abrirJanelaPartilhar({
          tipo: 'triagem',
          nome: r.codigo,
          partilhaId: r.partilha_id,
          workspaceNome: workspace?.name,
          aoMudar: () => setLerInfo((n) => n + 1),
        })
      }
    } catch (err) {
      setErroForm(err instanceof Error ? err.message : 'Não foi possível partilhar a triagem.')
    } finally {
      setOcupado(false)
    }
  }

  const onListar = async () => {
    if (lista) {
      setLista(null)
      return
    }
    setAListar(true)
    try {
      // Sem o módulo neste workspace só há as partilhadas comigo.
      setLista(comModulo ? await listarTriagens() : [])
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Não foi possível ler as triagens partilhadas.', {
        type: 'error',
      })
    } finally {
      setAListar(false)
    }
  }

  const onEntrar = (t: { id: string; codigo: string; nome: string | null }) => {
    if (entrarNaTriagem(t, (m) => window.confirm(m))) {
      setLista(null)
      pushToast(`Entrou na triagem partilhada "${t.codigo}".`, { type: 'success' })
    }
  }

  const onSair = () => {
    if (
      !window.confirm(
        'Deixar de sincronizar esta triagem? A cópia deste browser fica como está; '
          + 'a triagem partilhada continua no workspace para os outros.',
      )
    ) {
      return
    }
    sairDaTriagem()
  }

  const oferta = p.oferta
  // Uma oferta de partilhar (vinda de uma sessao) abre o formulario.
  // So abre ao pedir: a oferta de partilhar (vinda de uma sessao) fica
  // numa linha, para nao empurrar o resto do painel (pedido do Rui).
  const mostrarFormulario = aPartilhar && !p.activa
  const fecharFormulario = () => {
    setAPartilhar(false)
    if (oferta?.tipo === 'partilhar') p.definir({ oferta: null })
  }
  const pessoas = pessoasDistintas(p.presentes)
  const abertos = models.map((m) => ({ fileName: m.fileName, sha256: m.sha256 ?? '' }))

  return (
    <div
      data-testid="clash-partilha"
      className="flex shrink-0 flex-col gap-1 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px]"
    >
      {oferta?.tipo === 'entrar' && p.activa?.projetoId !== oferta.projetoId && (
        <div className="flex flex-wrap items-center gap-2 rounded bg-[var(--color-accent-bg)] px-2 py-1">
          <span className="flex-1">
            Esta sessão pertence à triagem partilhada «{oferta.codigo}» deste workspace.
          </span>
          <button
            type="button"
            className={BOTAO}
            onClick={() => onEntrar({ id: oferta.projetoId, codigo: oferta.codigo, nome: null })}
          >
            Entrar
          </button>
          <button type="button" className={BOTAO} onClick={() => p.definir({ oferta: null })}>
            Agora não
          </button>
        </div>
      )}
      {oferta?.tipo === 'partilhar' && !p.activa && (
        <div className="flex flex-wrap items-center gap-2 rounded bg-[var(--color-accent-bg)] px-2 py-1">
          <span className="flex-1">
            A sessão trouxe uma triagem com {oferta.pareceres} parecer
            {oferta.pareceres === 1 ? '' : 'es'}. Pode partilhá-la neste workspace para os
            colegas trabalharem nela ao mesmo tempo.
          </span>
          {comModulo && !aPartilhar && (
            <button type="button" className={BOTAO} onClick={() => setAPartilhar(true)}>
              Partilhar
            </button>
          )}
          <button type="button" className={BOTAO} onClick={fecharFormulario}>
            Agora não
          </button>
        </div>
      )}

      {p.activa ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${corDoEstado(p.estado)}`} aria-hidden />
          <span className="font-medium text-[var(--color-text)]" title={p.activa.nome ?? undefined}>
            {p.activa.codigo}
          </span>
          <span data-testid="clash-partilha-estado" className="text-[var(--color-text-muted)]">
            {textoDoEstado({ estado: p.estado, pessoas: Math.max(1, pessoas.length), pendentes: p.pendentes })}
          </span>
          {pessoas.length > 0 && (
            <span
              className="flex items-center gap-1 text-[var(--color-text-dim)]"
              title={pessoas.map((x) => x.nome).join(', ')}
            >
              <Users size={11} aria-hidden />
              {pessoas
                .map((x) => (x.utilizador === user?.id ? `${x.nome} (eu)` : x.nome))
                .join(', ')}
            </span>
          )}
          <span className="ml-auto flex gap-1">
            {info?.partilha_id && (
              <button
                type="button"
                className={`${BOTAO} flex items-center gap-1`}
                title={
                  info.pode_gerir
                    ? 'Escolher com quem a triagem está partilhada, ou retirar a partilha'
                    : 'Quem tem acesso a esta triagem'
                }
                onClick={() =>
                  abrirJanelaPartilhar({
                    tipo: 'triagem',
                    nome: info.codigo,
                    partilhaId: info.partilha_id ?? null,
                    workspaceNome: info.workspace_nome ?? undefined,
                    aoMudar: () => setLerInfo((n) => n + 1),
                  })
                }
              >
                <Share2 size={10} aria-hidden />
                {info.pode_gerir ? 'Partilhar com…' : 'Acessos'}
              </button>
            )}
            {(p.estado === 'sem-ligacao' || p.erro) && (
              <button type="button" className={BOTAO} onClick={() => motorDaPartilha()?.retomar()}>
                Tentar agora
              </button>
            )}
            <button
              type="button"
              className={BOTAO}
              aria-expanded={verEspecialidades}
              onClick={() => setVerEspecialidades((v) => !v)}
              title="Quem responde por cada especialidade (MAC, EST, HID, EEL, ARQ...) nesta triagem: recebem as notificações dos conflitos atribuídos à especialidade."
            >
              Especialidades
            </button>
            <button type="button" className={BOTAO} onClick={onSair}>
              Sair da partilha
            </button>
          </span>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-[var(--color-text-dim)]">Triagem só neste browser.</span>
          {comModulo && (
            <button type="button" className={BOTAO} onClick={() => setAPartilhar((v) => !v)}>
              Partilhar a triagem
            </button>
          )}
          <button type="button" className={BOTAO} onClick={() => void onListar()} disabled={aListar}>
            {aListar ? 'A ler…' : 'Triagens partilhadas'}
          </button>
        </div>
      )}

      {p.activa && verEspecialidades && (
        <EditorEspecialidades
          modulo="clash"
          contexto={p.activa.projetoId}
          aoFechar={() => setVerEspecialidades(false)}
        />
      )}
      {p.activa && info?.visivel_para && (
        <p className="truncate text-[10px] text-[var(--color-text-dim)]" title={info.visivel_para}>
          {info.visivel_para}
          {info.workspace_nome && workspace && info.workspace_nome !== workspace.name
            ? ` · do workspace ${info.workspace_nome}`
            : ''}
        </p>
      )}
      {p.activa && p.erro && <p className="text-[var(--color-error-text)]">{p.erro}</p>}
      {!p.activa && p.erro && <p className="text-[var(--color-text-dim)]">{p.erro}</p>}
      {p.activa && p.semCorrespondencia > 0 && (
        <p className="text-[10px] leading-snug text-[var(--color-text-dim)]">
          {p.semCorrespondencia} parecer{p.semCorrespondencia === 1 ? '' : 'es'} da triagem partilhada
          {p.semCorrespondencia === 1 ? ' é' : ' são'} de conflitos que não estão na corrida deste
          browser. Aparecem depois de detetar com os mesmos modelos e conjuntos.
        </p>
      )}
      {p.activa && p.semIdentidade > 0 && (
        <p className="text-[10px] leading-snug text-[var(--color-text-dim)]">
          {p.semIdentidade} parecer{p.semIdentidade === 1 ? '' : 'es'} deste browser ainda sem GlobalId:
          {p.semIdentidade === 1 ? ' é partilhado' : ' são partilhados'} depois de voltar a detetar.
        </p>
      )}

      {mostrarFormulario && (
        <form
          className="flex flex-col gap-1 rounded border border-[var(--color-border)] p-2"
          onSubmit={(e) => {
            e.preventDefault()
            void onPartilhar()
          }}
        >
          <p className="text-[var(--color-text-dim)]">
            A triagem deste browser (estados, prioridades, responsáveis, notas, grupos,
            classificação e histórico das corridas) passa a viver no servidor, para quem tiver
            acesso. Os modelos IFC não saem deste computador: cada pessoa corre a deteção com os
            seus, e a triagem casa pelos GlobalIds.
          </p>
          <label className="flex items-center gap-2">
            <span className="w-16 shrink-0 text-[var(--color-text-dim)]">Código</span>
            <input
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              maxLength={60}
              placeholder="Cha139"
              required
              className="flex-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-0.5"
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="w-16 shrink-0 text-[var(--color-text-dim)]">Nome</span>
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              maxLength={200}
              placeholder="Opcional"
              className="flex-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-0.5"
            />
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={workspaceInteiro}
              onChange={(e) => setWorkspaceInteiro(e.target.checked)}
            />
            Visível para todo o workspace (membros com Conflitos). Sem isto, fica só para si
            até escolher as pessoas, na janela que abre a seguir.
          </label>
          {erroForm && <p className="text-[var(--color-error-text)]">{erroForm}</p>}
          <div className="flex gap-1">
            <button
              type="submit"
              disabled={ocupado || !codigo.trim()}
              className="rounded bg-[var(--color-accent)] px-2 py-0.5 font-medium text-[var(--color-bg)] disabled:opacity-50"
            >
              {ocupado ? 'A partilhar…' : 'Partilhar'}
            </button>
            <button type="button" className={BOTAO} onClick={fecharFormulario}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {lista && !p.activa && (
        <div className="flex flex-col gap-1 rounded border border-[var(--color-border)] p-2">
          {doTipo(comigo.lista ?? [], 'triagem').map((i) => (
            <div key={i.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="font-medium text-[var(--color-text)]">{i.nome ?? 'Triagem'}</span>
              <span className="text-[var(--color-text-dim)]">partilhada consigo, {origemDoItem(i)}</span>
              <button
                type="button"
                className={`${BOTAO} ml-auto`}
                onClick={() => onEntrar({ id: i.recurso, codigo: i.nome ?? 'Triagem', nome: null })}
              >
                Entrar
              </button>
            </div>
          ))}
          {lista.length === 0 ? (
            doTipo(comigo.lista ?? [], 'triagem').length === 0 && (
              <p className="text-[var(--color-text-dim)]">
                Ainda não há triagens partilhadas consigo.
              </p>
            )
          ) : (
            lista.map((t) => {
              const comum = modelosEmComum(t.modelos, abertos)
              return (
                <div key={t.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="font-medium text-[var(--color-text)]">{t.codigo}</span>
                  {t.nome && <span className="text-[var(--color-text-muted)]">{t.nome}</span>}
                  <span className="text-[var(--color-text-dim)]">
                    {t.conflitos} conflito{t.conflitos === 1 ? '' : 's'} · modelos abertos{' '}
                    {comum}/{t.modelos.length}
                    {t.criado_por ? ` · ${t.criado_por}` : ''} · {dataCurta(t.actualizado_em)}
                    {t.visivel_para ? ` · ${t.visivel_para}` : ''}
                  </span>
                  <button type="button" className={`${BOTAO} ml-auto`} onClick={() => onEntrar(t)}>
                    Entrar
                  </button>
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}

/** "Ana está a editar este conflito": o bloqueio leve, so informativo. */
export function ClashPartilhaEdicao({ identidade }: { identidade: string | null }) {
  const { user } = useAuth()
  const presentes = useClashPartilha((s) => s.presentes)
  const nomes = quemEdita(presentes, identidade, user?.id ?? null)
  if (nomes.length === 0) return null
  return (
    <p className="text-[10px] text-[var(--color-warn-text)]">
      {nomes.join(', ')} {nomes.length === 1 ? 'está' : 'estão'} a editar este conflito. As
      alterações juntam-se campo a campo; no mesmo campo fica a última.
    </p>
  )
}
